import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { LuArrowLeft, LuCalendarDays, LuMapPin, LuEuro, LuTicket, LuUtensils, LuTrophy, LuUsers, LuExternalLink, LuNavigation, LuChevronDown, LuImage } from "react-icons/lu";
import "../../styles/events/eventsDetails.css"
import useEvents from "../../utils/useEvents";
import { getCategoryLabel, getCategoryKind, getCurrentEvents, getPastEvents, getEventStatus, HELLOASSO_URL, CATERING_TEXT } from "../../utils/events";
import { getFullWrittenDate, getHoursAndMinutes, getNumericDate, getShortWrittenMonth, getStoredNow } from "../../utils/date"


//DATE ET HEURES : "Samedi, 03 Octobre 2026, de 09h00 à 16h00", "Du … au …" sur plusieurs jours,
//ou seulement le début si la fin n'est pas renseignée
const formatEventDates = (event) => {
  if (!event.endDate) {
    return `${getFullWrittenDate(event.startDate)} à ${getHoursAndMinutes(event.startDate)}`;
  }
  const sameDay = new Date(event.startDate).toISOString().slice(0, 10) === new Date(event.endDate).toISOString().slice(0, 10);
  if (sameDay) {
    return `${getFullWrittenDate(event.startDate)}, de ${getHoursAndMinutes(event.startDate)} à ${getHoursAndMinutes(event.endDate)}`;
  }
  return `Du ${getFullWrittenDate(event.startDate)} à ${getHoursAndMinutes(event.startDate)} au ${getFullWrittenDate(event.endDate)} à ${getHoursAndMinutes(event.endDate)}`;
};

//PRIX : un simple nombre ("5") devient "5 €", un texte libre est affiché tel quel
const formatFees = (fees) => /^\d+([.,]\d+)?$/.test(fees.trim()) ? `${fees.trim()} €` : fees;

//DESCRIPTION : une ligne vide sépare deux paragraphes, un simple retour à la ligne est conservé
const DescriptionParagraphs = ({ text }) => text
  .split(/\n\s*\n/)
  .filter((paragraph) => paragraph.trim() !== "")
  .map((paragraph, index) => <p key={index}>{paragraph.trim()}</p>);

//TOURNOIS : on ignore ceux qui n'ont pas de type
const getTournaments = (event) => (event.tournament || []).filter((t) => t.type);

const getMapLink = (event) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([event.place, event.city].filter(Boolean).join(", "))}`;


//UN ÉVÈNEMENT À VENIR, EN DÉTAIL
function EventDetail({ event }) {

  const tournaments = getTournaments(event);

  return (
    <article className="event_detail surface">

      {/* AFFICHE + INSCRIPTION */}
      <div className="event_detail_aside">
        <div className="event_detail_poster">
          {event.poster
            ? <img src={event.poster} alt={`Affiche : ${event.title}`} />
            : <div className="event_detail_poster_empty"><LuImage aria-hidden="true" /><span>Affiche à venir</span></div>}
        </div>

        {/* Inscription préalable cochée : message + lien HelloAsso, sinon rien */}
        {event.subscription && (
          <div className="event_detail_subscription">
            <p className="event_detail_cta_title">Inscription obligatoire</p>
            <p className="event_detail_cta_text">Les inscriptions se font en ligne sur HelloAsso.</p>
            <a className="btn btn--primary btn--lg event_detail_cta_button" href={HELLOASSO_URL} target="_blank" rel="noopener noreferrer">
              S’inscrire sur HelloAsso <LuExternalLink aria-hidden="true" /><span className="sr-only">(nouvel onglet)</span>
            </a>
          </div>
        )}
      </div>

      {/* INFORMATIONS */}
      <div className="event_detail_body">
        <div className="event_detail_heading">
          {event.startDate && (
            <div className="event_detail_date" aria-hidden="true">
              <span className="event_detail_day">{getNumericDate(event.startDate)}</span>
              <span className="event_detail_month">{getShortWrittenMonth(event.startDate).replace(".", "")}</span>
            </div>
          )}
          <h2 className="title-xl event_detail_title">{event.title}</h2>
        </div>

        {(event.startDate || event.place || event.city || event.fees) && (
          <ul className="event_detail_facts">
            {/* Date, lieu et prix : affichés seulement s'ils sont renseignés */}
            {event.startDate && (
              <li>
                <span className="icon-tile icon-tile--red" aria-hidden="true"><LuCalendarDays /></span>
                <div>
                  <p className="event_detail_fact_label">Date</p>
                  <p className="event_detail_fact_value">{formatEventDates(event)}</p>
                </div>
              </li>
            )}
            {(event.place || event.city) && (
              <li>
                <span className="icon-tile icon-tile--red" aria-hidden="true"><LuMapPin /></span>
                <div>
                  <p className="event_detail_fact_label">Lieu</p>
                  <p className="event_detail_fact_value">{[event.place, event.city].filter(Boolean).join(", ")}</p>
                  <a className="link-arrow" href={getMapLink(event)} target="_blank" rel="noreferrer">
                    <LuNavigation aria-hidden="true" /> Itinéraire<span className="sr-only"> (nouvel onglet)</span>
                  </a>
                </div>
              </li>
            )}
            {event.fees && (
              <li>
                <span className="icon-tile icon-tile--red" aria-hidden="true"><LuEuro /></span>
                <div>
                  <p className="event_detail_fact_label">Participation</p>
                  <p className="event_detail_fact_value">{formatFees(event.fees)}</p>
                </div>
              </li>
            )}
          </ul>
        )}

        {event.description && (
          <div className="event_detail_description">
            <DescriptionParagraphs text={event.description} />
          </div>
        )}

        {tournaments.length > 0 && (
          <section className="event_detail_block" aria-label="Tournois">
            <h3 className="title-md">{tournaments.length > 1 ? "Les tournois" : "Le tournoi"}</h3>
            <ul className="event_tournaments">
              {tournaments.map((t) => (
                <li key={t._id || t.type} className="event_tournament">
                  <span className="icon-tile icon-tile--ink" aria-hidden="true"><LuTrophy /></span>
                  <div>
                    <p className="event_tournament_type">{t.type}</p>
                    <p className="event_tournament_limits">
                      {t.playersLimited && <span><LuUsers aria-hidden="true" /> {t.playersLimited} joueurs max. par équipe</span>}
                      {t.teamsLimited && <span><LuTicket aria-hidden="true" /> {t.teamsLimited} équipes max.</span>}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {event.catering === true && (
          <p className="event_detail_catering">
            <LuUtensils aria-hidden="true" /> {CATERING_TEXT}
          </p>
        )}
      </div>
    </article>
  );
};


//UNE ÉDITION PASSÉE (dépliable)
function PastEdition({ event }) {
  return (
    <details className="past_edition">
      <summary className="past_edition_summary">
        {event.poster
          ? <img className="past_edition_thumb" src={event.poster} alt="" />
          : <span className="past_edition_thumb past_edition_thumb--empty" aria-hidden="true"><LuImage /></span>}
        <span className="past_edition_heading">
          <span className="past_edition_title">{event.title}</span>
          <span className="past_edition_date">{[event.startDate && getFullWrittenDate(event.startDate), event.place].filter(Boolean).join(" · ")}</span>
        </span>
        <LuChevronDown className="past_edition_chevron" aria-hidden="true" />
      </summary>
      {event.description && (
        <div className="past_edition_content event_detail_description">
          <DescriptionParagraphs text={event.description} />
        </div>
      )}
    </details>
  );
};


function EventsDetails() {

  const events = useEvents();

  //CATÉGORIE SÉLECTIONNÉE DANS L'ACCUEIL OU LA PAGE ÉVÈNEMENTS
  const eventCategorySelected = useSelector((state) => state.eventCategoryReducer);

  //ÉVÈNEMENTS DE CETTE CATÉGORIE : en cours d'affichage (du plus proche au plus lointain) et passés (du plus récent au plus ancien)
  //Les évènements pas encore publiés (avant leur début d'affichage) n'apparaissent pas
  const now = getStoredNow();
  const categoryEvents = events.filter((e) => getCategoryLabel(e) === eventCategorySelected && getEventStatus(e, now) !== "scheduled");
  const upcomingEvents = getCurrentEvents(categoryEvents, now);
  const pastEvents = getPastEvents(categoryEvents, now);

  const editionsCount = categoryEvents.length;

  return (
    <main className="event_details_page">

      {/* EN-TÊTE */}
      <header className="page-hero">
        <div className="wrap page-hero_content">
          <Link to="/events" className="page-hero_back"><LuArrowLeft aria-hidden="true" /> Actualités</Link>
          <p className="page-hero_eyebrow">
            <span className="tricolor" aria-hidden="true"><span></span><span></span><span></span></span>
            {getCategoryKind(eventCategorySelected)}
          </p>
          <h1 className="page-hero_title">{eventCategorySelected || "Évènement"}</h1>
          {editionsCount > 0 && (
            <p className="page-hero_text">
              {upcomingEvents.length === 0
                ? "Pas de nouvelle date annoncée pour le moment."
                : upcomingEvents[0].startDate ? `Prochaine date : ${getFullWrittenDate(upcomingEvents[0].startDate)}` : "À l’affiche en ce moment"}
              {pastEvents.length > 0 && ` · ${pastEvents.length} ${pastEvents.length > 1 ? "éditions précédentes" : "édition précédente"}`}
            </p>
          )}
        </div>
      </header>

      <div className="wrap event_details_content">

        {/* ÉVÈNEMENTS À VENIR */}
        {upcomingEvents.map((event) => <EventDetail key={event._id} event={event} />)}

        {/* AUCUN ÉVÈNEMENT À VENIR */}
        {upcomingEvents.length === 0 && (
          <div className="event_details_empty surface">
            <p>{editionsCount > 0 ? "Aucune date à venir pour cet évènement. Retrouvez ci-dessous les éditions précédentes." : "Cet évènement est introuvable."}</p>
            <Link to="/events" className="link-arrow">Voir tous les évènements</Link>
          </div>
        )}

        {/* ÉDITIONS PRÉCÉDENTES */}
        {pastEvents.length > 0 && (
          <section className="past_editions" aria-labelledby="past-title">
            <h2 id="past-title" className="title-lg">Éditions précédentes</h2>
            <div className="past_editions_list">
              {pastEvents.map((event) => <PastEdition key={event._id} event={event} />)}
            </div>
          </section>
        )}
      </div>
    </main>
  )
};

export default EventsDetails;
